import RSDK from '@sr/functions/rsdk/src';
import htmlToMarkdown from '@sr/rich-text/html-to-markdown/src';
import markdownToHtml from '@sr/rich-text/markdown-to-html/src';
import { ContentType } from '@sr/types';
import Bold from '@tiptap/extension-bold';
import BulletList from '@tiptap/extension-bullet-list';
import Document from '@tiptap/extension-document';
import History from '@tiptap/extension-history';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import Paragraph from '@tiptap/extension-paragraph';
import Placeholder from '@tiptap/extension-placeholder';
import Text from '@tiptap/extension-text';
import Typography from '@tiptap/extension-typography';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import debounce from 'lodash.debounce';
import React, { useEffect } from 'react';
import Underlines from '../underlines/Underlines';
import MenuBar from './MenuBar';
import './styles.css';
import useStore from 'store';
import { useHasActiveBlockError } from 'store/selectors/message';
import useSaveElement from 'v1/hooks/use-save-element';
import { Box } from 'v1/ui';

// TipTap extensions to add to the Editor
const extensions = [
  Bold,
  BulletList,
  Document,
  History,
  Italic,
  Link.configure({
    // Don't add attributes. This is handled by the HTML to Markdown conversion in templates.
    HTMLAttributes: { target: null, rel: null },
  }),
  ListItem,
  OrderedList,
  Paragraph,
  Placeholder.configure({ placeholder: 'Add bullet points here.' }),
  Text,
  Typography,
];

type Props = {
  content: string;
  contentType: ContentType;
  updateContent: (content: string) => void;
  itemId: string;
};

export default function RichText({
  content,
  contentType,
  updateContent,
  itemId,
}: Props) {
  const handleFieldFocused = useStore.useHandleFieldFocused();

  const srId = RSDK.getItemPropSrId({
    itemId,
    sectionType: 'detail',
    contentType,
    prop: 'summary',
  });

  // Check if there is a block error for this srId
  const hasBlockError = useHasActiveBlockError(srId);

  const editor = useEditor({
    extensions,
    content: markdownToHtml(content),
    onFocus: () => handleFieldFocused(srId),
    editorProps: {
      attributes: {
        id: srId,
      },
    },
  });

  const [containerRef] = useSaveElement(srId);

  const debouncedSendUpdate = debounce((editor: Editor) => {
    const markdown = htmlToMarkdown(editor.getHTML());
    updateContent(markdown);
  }, 100);

  // Listen for updates from Firebase
  useEffect(() => {
    if (editor) {
      const oldMarkdown = htmlToMarkdown(editor.getHTML());
      if (content !== oldMarkdown) {
        const newHtml = markdownToHtml(content);
        editor.commands.setContent(newHtml);
      }
    }
  }, [content, editor]);

  // Listen for changes from RichText editor
  useEffect(() => {
    if (editor) {
      const update = () => {
        debouncedSendUpdate(editor);
      };
      editor.on('update', update);

      return () => {
        editor.off('update', update);
      };
    }
  }, [editor]); // eslint-disable-line

  if (!editor) return null;

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        '.ProseMirror': {
          borderColor: hasBlockError ? 'rgba(142, 0, 254, 1)' : undefined,
        },
      }}
    >
      <Underlines srId={srId} />
      <EditorContent editor={editor} className="editor-container" />
      <MenuBar editor={editor} />
    </Box>
  );
}
